import { useState } from 'preact/hooks'
import { useFieldArray, type UseFormReturn } from 'react-hook-form'

// editing/setEditing are owned by the parent so useAIForm (called before this hook)
// can reference editingBenefits without a circular dependency.

// react-hook-form passthrough: this hook is intentionally form-shape-agnostic; each caller
// (MeetingPanel, DemoPanel, DownloadPanel) supplies its own concrete form type. A bare `any` here is
// deliberate and preserves caller-side typing (FieldValues is invariant and rejects specific forms).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = any

export type UseBenefitsListReturn = {
  editing: boolean
  setEditing: (v: boolean) => void
  saving: boolean
  fields: { id: string; value: string }[]
  append: (value: { value: string }) => void
  remove: (index: number) => void
  move: (from: number, to: number) => void
  watchedBenefits: { value: string }[]
  hasDirtyEmpty: boolean
  benefitsChanged: boolean
  normInitial: string[]
  save: () => Promise<void>
  cancel: () => void
  // Passthrough from form for benefit input registration
  register: UseFormReturn<AnyForm>['register']
  getFieldState: UseFormReturn<AnyForm>['getFieldState']
  formState: UseFormReturn<AnyForm>['formState']
}

/**
 * Manages benefits list state, change detection, save/cancel handlers, and dirty-empty validation.
 * Drag-to-reorder position is not here — BenefitsCard holds that itself via useDragReorder, and only
 * the resulting `move` comes from this hook.
 *
 * Used by MeetingPanel, DemoPanel, and DownloadPanel.
 */
export function useBenefitsList({
  form,
  serverBenefits,
  editing,
  setEditing,
  onSave,
}: {
  form: UseFormReturn<AnyForm>
  serverBenefits: string[]
  editing: boolean
  setEditing: (v: boolean) => void
  onSave: (cleanBenefits: string[]) => Promise<boolean>
}): UseBenefitsListReturn {
  const [saving, setSaving] = useState(false)

  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'benefits' })

  const { register, getFieldState, formState } = form
  const watchedBenefits: { value: string }[] = form.watch('benefits') || []

  const hasDirtyEmpty = watchedBenefits.some((x: { value: string }, idx: number) => {
    const val = (x?.value ?? '').trim()
    const fs = getFieldState(`benefits.${idx}.value`, formState)
    return fs.isDirty && val.length === 0
  })

  const normInitial = (serverBenefits || []).map(s => s.trim()).filter(Boolean)
  const normCurrent = watchedBenefits.map((x: { value: string }) => (x?.value || '').trim()).filter(Boolean)
  const benefitsChanged = JSON.stringify(normInitial) !== JSON.stringify(normCurrent)

  const save = async () => {
    setSaving(true)
    const clean = (form.getValues('benefits') || [])
      .map((b: { value: string }) => (b?.value || '').trim())
      .filter(Boolean)
    const success = await onSave(clean)
    if (success) setEditing(false)
    setSaving(false)
  }

  const cancel = () => {
    setEditing(false)
    const next = (serverBenefits || []).map(b => ({ value: b }))
    form.reset({ ...form.getValues(), benefits: next })
  }

  return {
    editing, setEditing,
    saving,
    fields: fields as { id: string; value: string }[],
    append, remove, move,
    watchedBenefits,
    hasDirtyEmpty,
    benefitsChanged,
    normInitial,
    save, cancel,
    register, getFieldState, formState,
  }
}
